import React, { FC, Suspense } from 'react';
import ServiceCredits from 'src/components/pages/myAccount/serviceCredits';
import { getTestOrdersServiceConfig } from 'src/components/pages/myAccount/serviceCreditsConfigs';
import { Dialog, useDialogManager } from 'src/components/widgets/dialog';
import Loader from 'src/components/widgets/loader';
import { T } from 'src/shared/helpers';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';

interface TestOrdersPurchaseDialogProps {
    // Fired after a successful purchase (closes the dialog by default).
    onPurchased?: () => void;
    // Fired when the dialog is dismissed without purchasing (close button / overlay).
    onClose?: () => void;
    // Base URL the Shopify approval flow should return to (e.g. the current test product page).
    returnUrlAfterApproval?: string;
}

// Reusable modal that lets a seller buy the first Test Orders package. It renders the shared
// ServiceCredits component in its add-on layout, so the purchasing flow stays identical to the
// subscriptions page card.
const TestOrdersPurchaseDialog: FC<TestOrdersPurchaseDialogProps> = ({ onPurchased, onClose, returnUrlAfterApproval }) => {
    const { closeDialog, currentDialogId } = useDialogManager();
    const { shopifyEcommerceSettings, subscription, ecommerceName, salesChannels } = useSubscriptionData();

    const isShopify = ecommerceName?.toUpperCase() === 'SHOPIFY';
    const subscriptionCurrencyCode = isShopify ? (salesChannels.at(0)?.ecommerceSettings?.currencyCode ?? 'USD') : (subscription?.plan?.currencyCode ?? 'USD');

    const handleClose = () => {
        onClose?.();
        closeDialog(currentDialogId);
    };

    return (
        <Dialog title={T._('Buy test orders', 'Admin')} onClose={handleClose} showCloseButton>
            <Suspense fallback={<Loader />}>
                <ServiceCredits
                    variant='addon'
                    multiservice={false}
                    isShopify={isShopify}
                    subscriptionCurrencyCode={subscriptionCurrencyCode}
                    returnUrlAfterApproval={returnUrlAfterApproval}
                    services={[
                        getTestOrdersServiceConfig({
                            inTrial: subscription?.inTrial ?? false,
                            isShopify,
                            isTestStore: shopifyEcommerceSettings?.isTestStore === true
                        })
                    ]}
                    onPurchaseCompleted={() => {
                        onPurchased?.();
                        closeDialog(currentDialogId);
                    }}
                />
            </Suspense>
        </Dialog>
    );
};

export default TestOrdersPurchaseDialog;
